import React from 'react';
import { Box, Text, VStack } from '@chakra-ui/react';
import { COMPOSER_TAB_LABEL, ComposerTab } from './composerConstants.ts';
import { RecordComposerController } from './useRecordComposer.ts';
import { TaskComposerController } from './useTaskComposer.ts';
import ComposerTaskForm from './ComposerTaskForm.tsx';
import ComposerNoteEditor from './ComposerNoteEditor.tsx';

interface ComposerFormsProps {
	activeTab: ComposerTab;
	record: RecordComposerController;
	task: TaskComposerController;
	/** Whether the add region is expanded — drives focusing the note field. */
	isOpen: boolean;
	showTaskChildren?: boolean;
	onClearTaskParent?: () => void;
	additionalTab?: { tab: ComposerTab; form: React.ReactNode };
}

/**
 * Per-tab composer bodies. Records is wired to note entry; Task and System are
 * still placeholders until they are wired in a later pass.
 */
const ComposerForms: React.FC<ComposerFormsProps> = (props) => {
	const { activeTab, record, task, isOpen, showTaskChildren = true, onClearTaskParent, additionalTab } = props;

	if (additionalTab && activeTab === additionalTab.tab) return additionalTab.form;

	if (activeTab === ComposerTab.Record) {
		// The editor self-sizes and owns its own focus/caret, so the textarea auto-grow and
		// focus effects that lived here are no longer needed.
		return (
			<ComposerNoteEditor
				value={record.value}
				onChange={record.setValue}
				isPrivate={record.isPrivate}
				disabled={record.isSubmitting}
				shouldFocus={isOpen}
			/>
		);
	}

	if (activeTab === ComposerTab.Task) {
		return (
			<ComposerTaskForm
				task={task}
				shouldFocus={isOpen}
				showChildTasks={showTaskChildren}
				onClearParentTask={onClearTaskParent}
			/>
		);
	}

	return (
		<Box px={4} pb={4}>
			<VStack
				align="stretch"
				gap={2}
				borderRadius="xl"
				borderWidth="1px"
				borderStyle="dashed"
				borderColor="border.default"
				p={4}
				minH="96px"
				justify="center"
			>
				<Text textStyle="label">{COMPOSER_TAB_LABEL[activeTab]}</Text>
				<Text textStyle="micro" color="fg.subtle">
					Form coming in later pass — this is a placeholder.
				</Text>
			</VStack>
		</Box>
	);
};

export default ComposerForms;
